import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

// Icons
import { DollarSign, Check, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TenantCustomPricingDialog = ({ open, onOpenChange, tenant }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allModules, setAllModules] = useState([]);
  const [customPricing, setCustomPricing] = useState([]);

  useEffect(() => {
    if (open && tenant) {
      fetchData();
    }
  }, [open, tenant]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Récupérer tous les modules disponibles
      const modulesRes = await axios.get(`${API}/super-admin/module-pricing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Récupérer la config de prix personnalisés pour ce tenant
      const customPricingRes = await axios.get(`${API}/super-admin/tenant-custom-pricing/${tenant.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setAllModules(modulesRes.data || []);
      
      // Convertir les overrides en map pour faciliter l'accès
      const overridesMap = {};
      (customPricingRes.data.module_pricing_overrides || []).forEach(override => {
        overridesMap[override.module_id] = override;
      });
      
      // Initialiser customPricing avec tous les modules
      const initialCustomPricing = modulesRes.data.map(module => {
        const existingOverride = overridesMap[module.module_id];
        return {
          module_id: module.module_id,
          name: module.name,
          description: module.description,
          default_price: module.monthly_price,
          custom_price: existingOverride ? existingOverride.custom_price : module.monthly_price,
          use_default: existingOverride ? existingOverride.use_default : true,
          is_base_module: module.is_base_module
        };
      });
      
      setCustomPricing(initialCustomPricing);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (moduleId, newPrice) => {
    setCustomPricing(prev => prev.map(item => 
      item.module_id === moduleId 
        ? { ...item, custom_price: parseFloat(newPrice) || 0, use_default: false }
        : item
    ));
  };

  const handleToggleUseDefault = (moduleId) => {
    setCustomPricing(prev => prev.map(item => 
      item.module_id === moduleId 
        ? { ...item, use_default: !item.use_default }
        : item
    ));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Préparer les overrides à sauvegarder
      const module_pricing_overrides = customPricing.map(item => ({
        module_id: item.module_id,
        custom_price: item.custom_price,
        use_default: item.use_default
      }));
      
      await axios.put(
        `${API}/super-admin/tenant-custom-pricing/${tenant.id}`,
        { module_pricing_overrides },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Configuration de tarification enregistrée avec succès');
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving custom pricing:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-GN').format(price);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <DollarSign className="w-5 h-5 mr-2" />
            Configuration des Prix - {tenant?.name}
          </DialogTitle>
          <DialogDescription>
            Personnalisez les prix des modules optionnels pour cet établissement. 
            Par défaut, les prix standards seront appliqués.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Chargement...</p>
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Modules de Base */}
            <div>
              <h3 className="font-semibold text-lg mb-3">Modules de Base (Inclus)</h3>
              <div className="space-y-3">
                {customPricing.filter(item => item.is_base_module).map(item => (
                  <Card key={item.module_id} className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium">{item.name}</h4>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                        <div className="text-right ml-4">
                          <Badge variant="outline" className="bg-white">
                            Inclus dans Pack de Base
                          </Badge>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Modules Optionnels */}
            <div>
              <h3 className="font-semibold text-lg mb-3">Modules Optionnels</h3>
              <div className="space-y-3">
                {customPricing.filter(item => !item.is_base_module).map(item => (
                  <Card key={item.module_id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium">{item.name}</h4>
                          <p className="text-sm text-gray-600 mb-3">{item.description}</p>
                          
                          <div className="flex items-center space-x-4">
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">
                                Prix par défaut : {formatPrice(item.default_price)} GNF/mois
                              </Label>
                              
                              {!item.use_default && (
                                <div className="mt-2">
                                  <Label htmlFor={`price-${item.module_id}`} className="text-sm">
                                    Prix personnalisé (GNF/mois)
                                  </Label>
                                  <Input
                                    id={`price-${item.module_id}`}
                                    type="number"
                                    value={item.custom_price}
                                    onChange={(e) => handlePriceChange(item.module_id, e.target.value)}
                                    className="mt-1"
                                    placeholder="Ex: 100000"
                                  />
                                </div>
                              )}
                            </div>
                            
                            <Button
                              variant={item.use_default ? "outline" : "default"}
                              size="sm"
                              onClick={() => handleToggleUseDefault(item.module_id)}
                              className="flex items-center"
                            >
                              {item.use_default ? (
                                <>
                                  <X className="w-4 h-4 mr-1" />
                                  Personnaliser
                                </>
                              ) : (
                                <>
                                  <Check className="w-4 h-4 mr-1" />
                                  Utiliser défaut
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-blue-800">
                💡 <strong>Note :</strong> Les prix personnalisés seront automatiquement affichés au tenant 
                lors de sa souscription aux modules optionnels.
              </p>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-2 mt-6 pt-4 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annuler
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? 'Enregistrement...' : 'Enregistrer la Configuration'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TenantCustomPricingDialog;
